import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subject, take, takeUntil } from 'rxjs';
import { PublicFunctionsService } from 'app/mock-api/common/services/functions.service';
import {
    NewTrackerService,
    scheduleList,
    type Canvas,
    type GenerateQueryFromChartRequest,
} from 'app/company/tracker/new-tracker.service';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { ChatService } from '../../chat.service';
import {
    DynamicChartsComponent,
    type ChartData,
    type ChartSeriesEntry,
    type ChartSeriesMap,
} from 'app/ui/dynamic-charts/dynamic-charts.component';
import { MarkdownService } from 'app/modules/shared/services/markdown.service';
import { FormattingPipe } from 'app/modules/shared/services/formatting-data.pipe';
import { FuseCardComponent } from '@fuse/components/card';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';

export interface CreateTrackerFromChartPayload {
    chartId: number;
    query: string;
    dbType: string;
    name: string;
    dataSource: string;
    table: string;
    isExternal: boolean;
    type: number;
    period: string;
    threshold: string;
    thresholdType: string;
    question: string;
    query_explanation: string;
    canvasId: string;
    dateColumn: string;
    start_date: string;
    end_date: string;
    metricColumns: string[];
    selectedItems: { columnName: string; item: string[] }[];
}

export interface CreateTrackerFromChartDialogData {
    initial: CreateTrackerFromChartPayload;
    sourceQuery?: Record<string, unknown>;
    sourceChart?: Record<string, unknown>;
    agentDataSources?: unknown[];
}

interface GenerateQueryFromChartResponseData {
    query?: string;
    tableName?: string;
    originalMetricColumn?: string[];
    originalDateColumn?: string[];
    metricColumns?: string | string[];
    dateColumns?: string[];
    explanation?: string;
    trackerName?: string;
    isValid?: boolean;
}

@Component({
    selector: 'app-create-tracker-from-chart-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SaiDialogHeaderComponent,
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatDatepickerModule,
        TranslocoModule,
        DynamicChartsComponent,
        FuseCardComponent,
        DatePipe,
    ],
    providers: [FormattingPipe, DecimalPipe, DatePipe],
    templateUrl: './create-tracker-from-chart-dialog.component.html',
    styleUrl: './create-tracker-from-chart-dialog.component.scss',
})
export class CreateTrackerFromChartDialogComponent implements OnInit, OnDestroy {
    form: FormGroup;
    generating = false;
    confirming = false;
    loadingMetadata = false;
    loadingDates = false;
    generatedQuery = '';
    explanationQuery = '';
    generatedQueryData: GenerateQueryFromChartResponseData | null = null;
    queryTableData: Record<string, unknown>[] = [];
    queryChartData: ChartData | null = null;
    queryHeaders: string[] = [];
    queryData: unknown[][] = [];
    singleValueTracker: { name: string; value: unknown; measure?: string }[] =
        [];
    dateColumnOptions: { label: string; value: string }[] = [];
    scheduleList = scheduleList;
    typeList: { id: number; value: string }[] = [
        { id: 1, value: 'Time Series Tracker (Line Chart)' },
        { id: 2, value: 'Category Tracker (Bar Chart)' },
        { id: 3, value: 'Percentage Tracker (Pie Chart)' },
        { id: 4, value: 'Single Quantity Tracker (Single Value)' },
    ];
    canvasOptions: Canvas[] = [];
    minDate: Date | null = null;
    maxDate: Date | null = null;
    readonly initial: CreateTrackerFromChartPayload;
    private readonly _sourceQuery?: Record<string, unknown>;
    private readonly _sourceChart?: Record<string, unknown>;
    private readonly _agentDataSources: unknown[];

    private _destroy$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) data: CreateTrackerFromChartDialogData,
        private _dialogRef: MatDialogRef<CreateTrackerFromChartDialogComponent>,
        private _fb: FormBuilder,
        private _trackerService: NewTrackerService,
        private _chatService: ChatService,
        private _alert: CustomAlertService,
        private _cdr: ChangeDetectorRef,
        private _transloco: TranslocoService,
        public _functionsService: PublicFunctionsService,
        private _markdownService: MarkdownService
    ) {
        this.initial = data.initial;
        this._sourceQuery = data.sourceQuery;
        this._sourceChart = data.sourceChart;
        this._agentDataSources = data.agentDataSources ?? [];
        this.form = this._fb.group({
            name: [data.initial.name, [Validators.required, Validators.maxLength(500)]],
            dataSource: [{ value: data.initial.dataSource, disabled: true }],
            table: [{ value: data.initial.table, disabled: true }],
            question: [{ value: data.initial.question, disabled: true }],
            query_explanation: [
                { value: data.initial.query_explanation, disabled: true },
            ],
            chartId: [{ value: data.initial.chartId, disabled: true }],
            isExternal: [{ value: data.initial.isExternal, disabled: true }],
            type: [data.initial.type, Validators.required],
            period: [data.initial.period || 'daily'],
            threshold: [data.initial.threshold],
            thresholdType: [data.initial.thresholdType],
            canvasId: [data.initial.canvasId || ''],
            dateColumn: [data.initial.dateColumn],
            start_date: [this.parseDateString(data.initial.start_date)],
            end_date: [this.parseDateString(data.initial.end_date)],
            metricColumnsText: [
                (data.initial.metricColumns ?? []).join(', '),
            ],
        });

        const chartType = this.resolveChartType(undefined, this._sourceChart);
        const trackerType = this.mapChartTypeToTrackerType(chartType);
        if (trackerType != null && !data.initial.type) {
            this.form.get('type')?.setValue(trackerType);
        }
    }

    ngOnInit(): void {
        this._dialogRef.updateSize('min(96vw, 1180px)', '90dvh');
        this.ensureDataSource();

        this._trackerService
            .refreshCanvases()
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe(() => {
                this.canvasOptions = this._trackerService.canvases();
                this._cdr.markForCheck();
            });

        this.loadQueryMetadata();
    }

    onDateColumnSelected(column: string | null | undefined): void {
        const apiColumn = String(column ?? '').trim();
        if (apiColumn) {
            this.fetchMinMaxDates(apiColumn);
        }
    }

    private resolveMetadataQueryId(): number {
        const q = this._sourceQuery;
        if (!q || typeof q !== 'object') {
            return 0;
        }
        return Number(q['id'] ?? q['queryId'] ?? 0);
    }

    private loadQueryMetadata(): void {
        const queryId = this.resolveMetadataQueryId();
        if (!queryId) {
            return;
        }

        this.loadingMetadata = true;
        this._cdr.markForCheck();

        this._chatService
            .extractQueryMetadata({
                queryId,
                dbType: this.initial.dbType ?? '',
            })
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (res: any) => {
                    this.applyQueryMetadata(res?.data ?? res);
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    this.loadingMetadata = false;
                    this._alert.failedMessage(
                        err?.error?.message ??
                            'Failed to extract query metadata'
                    );
                    this._cdr.markForCheck();
                },
            });
    }

    private applyQueryMetadata(data: any): void {
        if (!data) {
            this.loadingMetadata = false;
            return;
        }

        const table = data.tableName ?? data.table;
        if (table) {
            const tableName = String(table);
            this.initial.table = tableName;
            this.form.get('table')?.setValue(tableName);
        }

        this.ensureDataSource(data);

        const chartType = this.resolveChartType(
            data.chartType ?? data.charttype,
            this._sourceChart
        );
        const trackerType = this.mapChartTypeToTrackerType(chartType);
        if (trackerType != null) {
            this.form.get('type')?.setValue(trackerType);
        }

        const dateColumns = this.parseDateColumns(data);
        if (dateColumns.length > 0) {
            this.loadingMetadata = false;
            this.setDateColumnOptions(dateColumns);
        } else {
            this.loadDateColumnsFromTable();
        }
    }

    /** Reads `dateColumn` / `dateColumns` from extract-query-metadata response. */
    private parseDateColumns(data: any): { label: string; value: string }[] {
        const raw = data?.dateColumn ?? data?.dateColumns;
        if (raw == null) {
            return [];
        }
        if (Array.isArray(raw)) {
            return raw
                .map(col => this.toDateColumnOption(col))
                .filter(opt => opt.value.length > 0);
        }
        const single = this.toDateColumnOption(raw);
        return single.value ? [single] : [];
    }

    private toDateColumnOption(col: unknown): { label: string; value: string } {
        if (typeof col === 'string') {
            const value = col.trim();
            return { label: value, value };
        }
        if (col && typeof col === 'object') {
            const item = col as { columnName?: string; fieldName?: string };
            const apiValue = String(item.columnName ?? item.fieldName ?? '').trim();
            const label = this.initial.isExternal
                ? apiValue
                : String(item.fieldName ?? item.columnName ?? '').trim();
            return { label: label || apiValue, value: apiValue };
        }
        return { label: '', value: '' };
    }

    private setDateColumnOptions(
        columns: { label: string; value: string }[]
    ): void {
        this.dateColumnOptions = columns;
        const current = String(this.form.get('dateColumn')?.value ?? '').trim();
        const preferred =
            columns.length === 1
                ? columns[0].value
                : columns.find(col => col.value === current)?.value ??
                  columns[0]?.value ??
                  '';

        if (preferred) {
            this.form.get('dateColumn')?.setValue(preferred, {
                emitEvent: false,
            });
            this.onDateColumnSelected(preferred);
        } else {
            this.form.get('dateColumn')?.reset('', { emitEvent: false });
        }
    }

    /** When extract-query-metadata returns null dateColumn, load columns from the table. */
    private loadDateColumnsFromTable(): void {
        this.ensureDataSource();

        const table = this.initial.table;
        const dataSource = this.initial.dataSource;
        if (!table || !dataSource) {
            this.loadingMetadata = false;
            return;
        }

        this._trackerService
            .getMetricasAndDates({
                table,
                dataSource,
                isExternal: this.initial.isExternal,
            })
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (res) => {
                    this.loadingMetadata = false;
                    this.setDateColumnOptions(
                        this.mapMetricDateColumns(res?.dates)
                    );
                    this._cdr.markForCheck();
                },
                error: () => {
                    this.loadingMetadata = false;
                    this._cdr.markForCheck();
                },
            });
    }

    private mapMetricDateColumns(
        dates: any[]
    ): { label: string; value: string }[] {
        if (!Array.isArray(dates)) {
            return [];
        }
        return dates
            .map(item => this.toDateColumnOption(item))
            .filter(opt => opt.value.length > 0);
    }

    private resolveChartType(
        metadataChartType: unknown,
        chart?: Record<string, unknown>
    ): string {
        const fromMetadata = String(metadataChartType ?? '')
            .toLowerCase()
            .trim();
        if (fromMetadata) {
            return fromMetadata;
        }

        if (!chart) {
            return '';
        }

        const fromGraphType = String(chart['graphType'] ?? '')
            .toLowerCase()
            .trim();
        if (fromGraphType) {
            return fromGraphType;
        }

        const series = chart['series'];
        if (!series || typeof series !== 'object') {
            return '';
        }

        const seriesMap = series as Record<string, unknown>;
        const defaultSeries = Array.isArray(seriesMap['default'])
            ? seriesMap['default']
            : [];
        if (defaultSeries.length > 0) {
            const fromDefault = String(
                (defaultSeries[0] as Record<string, unknown>)?.['type'] ?? ''
            )
                .toLowerCase()
                .trim();
            if (fromDefault) {
                return fromDefault;
            }
        }

        for (const key of Object.keys(seriesMap)) {
            const raw = seriesMap[key];
            if (!Array.isArray(raw) || raw.length === 0) {
                continue;
            }
            const fromSeries = String(
                (raw[0] as Record<string, unknown>)?.['type'] ?? ''
            )
                .toLowerCase()
                .trim();
            if (fromSeries) {
                return fromSeries;
            }
        }

        return '';
    }

    private mapChartTypeToTrackerType(chartType: string): number | null {
        if (!chartType) {
            return null;
        }
        if (chartType === 'line') {
            return 1;
        }
        if (chartType === 'bar') {
            return 2;
        }
        if (chartType === 'pie' || chartType === 'polararea') {
            return 3;
        }
        return null;
    }

    private buildFiltersObject(): Record<string, string[]> {
        const filters: Record<string, string[]> = {};
        for (const item of this.initial.selectedItems ?? []) {
            const column = item?.columnName;
            if (!column || !Array.isArray(item.item)) {
                continue;
            }
            filters[column] = item.item.map(value => String(value).trim());
        }
        return filters;
    }

    private ensureDataSource(metadata?: Record<string, unknown>): void {
        if (String(this.initial.dataSource ?? '').trim()) {
            return;
        }

        const resolved = this.resolveDataSourceId(
            metadata,
            this._sourceQuery,
            this.initial
        );
        if (!resolved) {
            return;
        }

        this.initial.dataSource = resolved;
        this.form.get('dataSource')?.setValue(resolved);
    }

    private resolveDataSourceId(...sources: unknown[]): string {
        const readValue = (value: unknown): string => {
            if (value == null) {
                return '';
            }
            if (typeof value === 'string' || typeof value === 'number') {
                const normalized = String(value).trim();
                if (
                    !normalized ||
                    normalized === 'undefined' ||
                    normalized === 'null'
                ) {
                    return '';
                }
                return normalized;
            }
            if (typeof value === 'object') {
                const obj = value as Record<string, unknown>;
                for (const key of [
                    'eid',
                    'id',
                    'dataSourceId',
                    'dataSource',
                    'datasourceId',
                ]) {
                    const nested = readValue(obj[key]);
                    if (nested) {
                        return nested;
                    }
                }
            }
            return '';
        };

        for (const source of sources) {
            if (!source || typeof source !== 'object') {
                continue;
            }
            const obj = source as Record<string, unknown>;
            for (const key of [
                'dataSourceId',
                'datasourceId',
                'datasource_id',
                'data_source_id',
                'dsId',
                'ds_id',
                'DsEid',
                'dataSource',
                'datasource',
                'data_source',
            ]) {
                const resolved = readValue(obj[key]);
                if (resolved) {
                    return resolved;
                }
            }
        }

        const tableName = String(this.initial.table ?? '').trim();
        if (!tableName || !this._agentDataSources.length) {
            return '';
        }

        for (const ds of this._agentDataSources) {
            if (!ds || typeof ds !== 'object') {
                continue;
            }
            const datasource = ds as Record<string, unknown>;
            const dsId = readValue(datasource.eid ?? datasource.id);
            if (!dsId) {
                continue;
            }

            const tableLists = [
                datasource.tables,
                datasource.external_tables,
            ];
            const hasTable = tableLists.some((list) => {
                if (!Array.isArray(list)) {
                    return false;
                }
                return list.some((table) => {
                    if (typeof table === 'string') {
                        return table === tableName;
                    }
                    if (table && typeof table === 'object') {
                        const row = table as Record<string, unknown>;
                        return (
                            row.name === tableName ||
                            row.value === tableName ||
                            row.tableName === tableName
                        );
                    }
                    return false;
                });
            });

            if (hasTable) {
                return dsId;
            }
        }

        return '';
    }

    private parseDateString(
        value: string | Date | null | undefined
    ): Date | null {
        if (!value) {
            return null;
        }
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }

        const normalized = String(value).trim();
        if (!normalized) {
            return null;
        }

        const ddMmYyyy = normalized.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (ddMmYyyy) {
            return new Date(
                Number(ddMmYyyy[3]),
                Number(ddMmYyyy[2]) - 1,
                Number(ddMmYyyy[1])
            );
        }

        const yyyyMmDd = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (yyyyMmDd) {
            return new Date(
                Number(yyyyMmDd[1]),
                Number(yyyyMmDd[2]) - 1,
                Number(yyyyMmDd[3])
            );
        }

        const parsed = new Date(normalized);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    private fetchMinMaxDates(column: string): void {
        this.ensureDataSource();

        const table = String(this.initial.table ?? '').trim();
        const dataSource = String(this.initial.dataSource ?? '').trim();
        if (!column || !table || !dataSource) {
            return;
        }

        this.loadingDates = true;
        this._cdr.markForCheck();

        const modal = {
            table,
            dataSource,
            isExternal: this.initial.isExternal,
            column,
            filters: this.buildFiltersObject(),
        };

        this._trackerService
            .getMinMaxData(modal)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (res: any) => {
                    this.loadingDates = false;
                    const min = res?.data?.min;
                    const max = res?.data?.max;
                    this.minDate = this.parseDateString(min);
                    this.maxDate = this.parseDateString(max);
                    if (this.minDate) {
                        this.form.get('start_date')?.setValue(this.minDate);
                    }
                    if (this.maxDate) {
                        this.form.get('end_date')?.setValue(this.maxDate);
                    }
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    this.loadingDates = false;
                    this._alert.failedMessage(
                        err?.error?.message ?? 'Failed to load date range'
                    );
                    this._cdr.markForCheck();
                },
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    close(): void {
        this._dialogRef.close(false);
    }

    backToConfigure(): void {
        this.generatedQuery = '';
        this.explanationQuery = '';
        this.generatedQueryData = null;
        this.queryHeaders = [];
        this.queryData = [];
        this.queryChartData = null;
        this.singleValueTracker = [];
        this._cdr.markForCheck();
    }

    get trackerType(): number {
        return Number(this.form.get('type')?.value ?? 0);
    }

    get hasPreviewData(): boolean {
        if (this.trackerType === 4) {
            return this.singleValueTracker.length > 0;
        }
        return this.queryHeaders.length > 0 || this.queryChartData != null;
    }

    markDownContent(value: string): string {
        return this._markdownService.processMarkedText(value);
    }

    trackByHeader(_index: number, header: string): string {
        return header;
    }

    trackByRow(index: number): number {
        return index;
    }

    private formatFormDateForApi(value: unknown): string {
        if (!value) {
            return '';
        }
        if (typeof value === 'object' && value !== null && 'toJSDate' in value) {
            const jsDate = (value as { toJSDate: () => Date }).toJSDate();
            return jsDate instanceof Date
                ? this._functionsService.formatDate(jsDate)
                : '';
        }
        if (value instanceof Date) {
            return this._functionsService.formatDate(value);
        }
        const parsed = this.parseDateString(
            typeof value === 'string' ? value : String(value)
        );
        return parsed ? this._functionsService.formatDate(parsed) : '';
    }

    private buildGenerateQueryFromChartBody(): GenerateQueryFromChartRequest {
        const v = this.form.getRawValue();
        const metricColumns = String(v.metricColumnsText ?? '')
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);
        const startDate = this.formatFormDateForApi(v.start_date);
        const endDate = this.formatFormDateForApi(v.end_date);
        const chartId = Number(this.initial.chartId);
        const trackerType =
            v.type != null && v.type !== '' ? Number(v.type) : null;

        return {
            chartId: chartId > 0 ? chartId : null,
            question: String(this.initial.question ?? '').trim() || null,
            table: String(this.initial.table ?? '').trim() || null,
            dataSource: String(this.initial.dataSource ?? '').trim() || null,
            period: String(v.period ?? '').trim() || null,
            metricColumns,
            dateColumn: String(v.dateColumn ?? '').trim() || null,
            start_date: startDate || null,
            end_date: endDate || null,
            trackerType:
                trackerType != null && !Number.isNaN(trackerType)
                    ? trackerType
                    : null,
            isExternal:
                typeof this.initial.isExternal === 'boolean'
                    ? this.initial.isExternal
                    : null,
            selectedItems: this.initial.selectedItems ?? [],
            trackerName: String(v.name ?? '').trim() || null,
        };
    }

    generateAndExecute(): void {
        if (this.form.invalid || this.generating) {
            this.form.markAllAsTouched();
            return;
        }

        const body = this.buildGenerateQueryFromChartBody();

        this.generating = true;
        this._cdr.markForCheck();
        this._trackerService
            .generateQueryFromChart(body)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (res) => {
                    const data = (res?.data ??
                        {}) as GenerateQueryFromChartResponseData;
                    this.generatedQueryData = data;
                    this.generatedQuery = String(data.query ?? '').trim();
                    this.explanationQuery = String(data.explanation ?? '').trim();

                    if (data.trackerName) {
                        this.form.get('name')?.setValue(data.trackerName);
                    }

                    if (!this.generatedQuery) {
                        this.generating = false;
                        this._alert.failedMessage(
                            'Failed to generate tracker query from chart'
                        );
                        this._cdr.markForCheck();
                        return;
                    }

                    this.executeQuery();
                },
                error: (err) => {
                    this.generating = false;
                    this._alert.failedMessage(
                        err?.error?.message ??
                            'Failed to generate tracker query from chart'
                    );
                    this._cdr.markForCheck();
                },
            });
    }

    private buildExecuteQueryBody(): Record<string, unknown> {
        const v = this.form.getRawValue();
        return {
            question: this.initial.question,
            table: this.initial.table,
            dataSource: this.initial.dataSource,
            query: this.generatedQuery,
            trackerType: Number(v.type),
            isExternal: this.initial.isExternal,
        };
    }

    private normalizeChartData(chart: unknown): ChartData | null {
        if (!chart || typeof chart !== 'object') {
            return null;
        }

        const raw = chart as ChartData & { data?: unknown };

        // Preferred: backend returned a ready-to-render series map + labels array.
        if (
            this.isRenderableSeriesMap(raw.series) &&
            Array.isArray(raw.labels) &&
            raw.labels.length > 0
        ) {
            return {
                ...raw,
                id: raw.id ? String(raw.id) : `tracker-preview-${Date.now()}`,
            };
        }

        // Fallback: chart payload carries raw rows (e.g. `series`/`labels` sent as
        // flags with the data under `data`). Build the model from those rows.
        if (Array.isArray(raw.data) && raw.data.length > 0) {
            return this.buildChartDataFromRows(
                raw.data as Record<string, unknown>[],
                this.trackerType,
                {
                    xKey: typeof raw.xAxis === 'string' ? raw.xAxis : undefined,
                    yKey: typeof raw.yAxis === 'string' ? raw.yAxis : undefined,
                    color: raw.color,
                    title: raw.title,
                }
            );
        }

        return null;
    }

    private isRenderableSeriesMap(series: unknown): series is ChartSeriesMap {
        if (!series || typeof series !== 'object') {
            return false;
        }
        return Object.values(series as Record<string, unknown>).some(
            value => Array.isArray(value) && value.length > 0
        );
    }

    /**
     * Builds a `ChartData` model from execute-query result rows so the preview
     * still renders when the backend does not return a ready `chart.series` map.
     * First (or `xKey`) column is the category/label axis; numeric columns become series.
     */
    private buildChartDataFromRows(
        rows: Record<string, unknown>[],
        trackerType: number,
        meta?: {
            xKey?: string;
            yKey?: string;
            color?: string;
            title?: string;
        }
    ): ChartData | null {
        if (!Array.isArray(rows) || rows.length === 0) {
            return null;
        }

        const headers = Object.keys(rows[0] ?? {});
        if (headers.length === 0) {
            return null;
        }

        const labelKey =
            meta?.xKey && headers.includes(meta.xKey) ? meta.xKey : headers[0];

        const numericKeys = headers.filter(
            header =>
                header !== labelKey &&
                rows.some(row => this.isNumericValue(row[header]))
        );
        const valueKeys =
            numericKeys.length > 0
                ? numericKeys
                : headers.filter(header => header !== labelKey);
        if (valueKeys.length === 0) {
            return null;
        }

        const chartType =
            trackerType === 1 ? 'line' : trackerType === 3 ? 'pie' : 'bar';
        const labels = rows.map(row => row[labelKey]);

        let series: ChartSeriesMap;
        if (chartType === 'pie') {
            const pieKey =
                meta?.yKey && valueKeys.includes(meta.yKey)
                    ? meta.yKey
                    : valueKeys[0];
            series = {
                default: [
                    {
                        name: pieKey,
                        type: 'pie',
                        data: rows.map(row => this.toNumberOrNull(row[pieKey])),
                    } as ChartSeriesEntry,
                ],
            };
        } else {
            series = {
                default: valueKeys.map(
                    key =>
                        ({
                            name: key,
                            type: chartType,
                            data: rows.map(row =>
                                this.toNumberOrNull(row[key])
                            ),
                        }) as ChartSeriesEntry
                ),
            };
        }

        return {
            id: `tracker-preview-${Date.now()}`,
            xAxis: labelKey,
            yAxis: meta?.yKey ?? valueKeys[0],
            labels,
            series,
            color: meta?.color,
            title: meta?.title,
        };
    }

    private isNumericValue(value: unknown): boolean {
        if (typeof value === 'number') {
            return Number.isFinite(value);
        }
        if (typeof value === 'string' && value.trim() !== '') {
            return Number.isFinite(Number(value));
        }
        return false;
    }

    private toNumberOrNull(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    }

    private executeQuery(): void {
        this._trackerService
            .executeQuery(this.buildExecuteQueryBody())
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                // `executeQuery` decodes msgpack, which is genuinely `unknown` at the source.
                // The record assumption belongs here, at the consumer that relies on it.
                next: (raw) => {
                    const res = raw as Record<string, unknown>;
                    this.generating = false;
                    const trackerType = this.trackerType;

                    if (
                        trackerType === 1 ||
                        trackerType === 2 ||
                        trackerType === 3
                    ) {
                        const rows = Array.isArray(res?.data)
                            ? (res.data as Record<string, unknown>[])
                            : [];
                        this.queryTableData = rows;
                        this.queryChartData =
                            this.normalizeChartData(res?.chart) ??
                            this.buildChartDataFromRows(rows, trackerType);
                        this.transformQueryTableContent(this.queryTableData);
                    } else if (trackerType === 4) {
                        this.singleValueTracker = Array.isArray(res?.data)
                            ? (res.data as {
                                  name: string;
                                  value: unknown;
                                  measure?: string;
                              }[])
                            : [];
                    }

                    this._cdr.markForCheck();
                },
                error: (error: HttpErrorResponse) => {
                    this.generating = false;
                    let message = 'Failed to execute tracker query';
                    if (error.error instanceof ArrayBuffer) {
                        try {
                            const decoded = new TextDecoder('utf-8').decode(
                                error.error
                            );
                            const parsed = JSON.parse(decoded) as {
                                message?: string;
                            };
                            message = parsed.message ?? message;
                        } catch {
                            // keep default message
                        }
                    } else if (error.error?.message) {
                        message = error.error.message;
                    }
                    this._alert.failedMessage(message);
                    this._cdr.markForCheck();
                },
            });
    }

    private transformQueryTableContent(
        queryTableData: Record<string, unknown>[]
    ): void {
        if (!queryTableData.length) {
            this.queryHeaders = [];
            this.queryData = [];
            return;
        }

        this.queryHeaders = Object.keys(queryTableData[0]);
        this.queryData = queryTableData.map(item =>
            this.queryHeaders.map((header) => {
                let value: unknown = item[header];
                if (value === '' || value === null || value === undefined) {
                    value = null;
                }
                if (header === 'last_update' && typeof value === 'string') {
                    value = value.split('T')[0];
                }
                return value;
            })
        );
    }

    private resolveMetricColumnsForCreate(): string[] {
        const fromForm = String(this.form.get('metricColumnsText')?.value ?? '')
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);
        if (fromForm.length > 0) {
            return fromForm;
        }

        const fromGenerated = this.generatedQueryData?.metricColumns;
        if (Array.isArray(fromGenerated)) {
            return fromGenerated.map(col => String(col).trim()).filter(Boolean);
        }
        if (typeof fromGenerated === 'string' && fromGenerated.trim()) {
            return [fromGenerated.trim()];
        }
        return [];
    }

    private buildTrackerPayload(): Record<string, unknown> {
        const v = this.form.getRawValue();
        const payload: Record<string, unknown> = {
            name: String(v.name ?? '').trim(),
            table: this.initial.table,
            query: this.generatedQuery,
            dataSource: this.initial.dataSource,
            question: this.initial.question,
            period: v.period,
            threshold: v.threshold,
            thresholdType: v.thresholdType,
            type: Number(v.type),
            metricColumns: this.resolveMetricColumnsForCreate(),
            dateColumn: v.dateColumn,
            originalMetricColumn:
                this.generatedQueryData?.originalMetricColumn ?? [],
            originalDateColumn:
                this.generatedQueryData?.originalDateColumn ?? [],
            isExternal: this.initial.isExternal,
            query_explanation:
                this.explanationQuery ||
                this.generatedQueryData?.explanation ||
                this.initial.query_explanation,
            start_date: this.formatFormDateForApi(v.start_date) || null,
            end_date: this.formatFormDateForApi(v.end_date) || null,
        };

        if (v.canvasId) {
            payload.canvasId = String(v.canvasId);
        }

        return payload;
    }

    confirmCreate(): void {
        if (this.confirming || !this.generatedQuery || !this.hasPreviewData) {
            return;
        }

        this.confirming = true;
        this._cdr.markForCheck();
        this._trackerService
            .addNewTracker(this.buildTrackerPayload())
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (res: { message?: string }) => {
                    this.confirming = false;
                    const msg =
                        res?.message ||
                        this._transloco.translate('Tracker created');
                    this._alert.successMessage(msg);
                    this._dialogRef.close(true);
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    this.confirming = false;
                    this._alert.failedMessage(
                        err?.error?.message ?? 'Failed to create tracker'
                    );
                    this._cdr.markForCheck();
                },
            });
    }
}
